// src/hooks/useChildren.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

// =====================================================
// QUERIES (Read operations)
// =====================================================

/**
 * Get all children for a specific branch
 */
export const useChildren = (branchId, options = {}) => {
  return useQuery({
    queryKey: ['children', branchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('children')
        .select('*')
        .eq('branch_id', branchId)
        .order('last_name', { ascending: true });

      if (error) throw error;
      return data;
    },
    enabled: !!branchId,
    ...options
  });
};

/**
 * Get all children (for super admin)
 */
export const useAllChildren = (options = {}) => {
  return useQuery({
    queryKey: ['children', 'all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('children')
        .select('*')
        .order('last_name', { ascending: true });

      if (error) throw error;
      return data;
    },
    ...options
  });
};

/**
 * Get children for current user (parent view)
 */
export const useMyChildren = (userId, options = {}) => {
  return useQuery({
    queryKey: ['children', 'my', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('children')
        .select('*')
        .contains('parent_ids', [userId])
        .order('last_name', { ascending: true });

      if (error) throw error;
      return data;
    },
    enabled: !!userId,
    ...options
  });
};

/**
 * Get single child by ID
 */
export const useChild = (childId, options = {}) => {
  return useQuery({
    queryKey: ['children', childId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('children')
        .select('*')
        .eq('id', childId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!childId,
    ...options
  });
};

/**
 * Get children by group (UPDATED: using group_id instead of group_ids)
 */
export const useGroupChildren = (groupId, options = {}) => {
  return useQuery({
    queryKey: ['children', 'group', groupId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('children')
        .select('*')
        .eq('group_id', groupId)  // ✅ CHANGED: eq instead of contains
        .order('last_name', { ascending: true});

      if (error) throw error;
      return data;
    },
    enabled: !!groupId,
    ...options
  });
};

/**
 * Get children in the given groups (for teacher Podsumowanie filter)
 */
export const useChildrenByGroups = (groupIds = [], options = {}) => {
  return useQuery({
    queryKey: ['children', 'by-groups', groupIds],
    queryFn: async () => {
      if (!groupIds || groupIds.length === 0) return [];
      const { data, error } = await supabase
        .from('children')
        .select('*')
        .in('group_id', groupIds)
        .order('last_name', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!groupIds?.length,
    ...options
  });
};

/**
 * Get children with pending verification
 */
export const usePendingChildren = (branchId, options = {}) => {
  return useQuery({
    queryKey: ['children', 'pending', branchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('children')
        .select('*')
        .eq('branch_id', branchId)
        .eq('verification_status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!branchId,
    ...options
  });
};

/**
 * Get orphaned children (no parents assigned)
 */
export const useOrphanedChildren = (options = {}) => {
  return useQuery({
    queryKey: ['children', 'orphaned'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('children')
        .select('*')
        .or('parent_ids.is.null,parent_ids.eq.{}')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    ...options
  });
};

/**
 * Get children without groups
 */
export const useChildrenWithoutGroup = (branchId, options = {}) => {
  return useQuery({
    queryKey: ['children', 'no-group', branchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('children')
        .select('*')
        .eq('branch_id', branchId)
        .is('group_id', null)  // ✅ CHANGED: checking group_id is null
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!branchId,
    ...options
  });
};

// =====================================================
// MUTATIONS (Write operations)
// =====================================================

/**
 * Create a new child
 */
export const useCreateChild = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (childData) => {
      const { data, error } = await supabase
        .from('children')
        .insert(childData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['children'] });
      toast.success('Child added successfully!');
    },
    onError: (error) => {
      logger.error('Create child error', { message: error.message });
      toast.error(`Failed to add child: ${error.message}`);
    }
  });
};

/**
 * Update a child
 */
export const useUpdateChild = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }) => {
      const { data, error } = await supabase
        .from('children')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['children'] });
      queryClient.setQueryData(['children', variables.id], data);
      toast.success('Child updated successfully!');
    },
    onError: (error) => {
      logger.error('Update child error', { message: error.message });
      toast.error(`Failed to update child: ${error.message}`);
    }
  });
};

/**
 * Delete (archive) a child
 */
export const useDeleteChild = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (childId) => {
      const { error } = await supabase
        .from('children')
        .update({ status: 'archived' })
        .eq('id', childId);

      if (error) throw error;
      return childId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['children'] });
      toast.success('Child archived successfully!');
    },
    onError: (error) => {
      logger.error('Delete child error', { message: error.message });
      toast.error(`Failed to archive child: ${error.message}`);
    }
  });
};

/**
 * Verify a child (change status from pending to verified)
 */
export const useVerifyChild = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ childId, verified }) => {
      const { data, error } = await supabase
        .from('children')
        .update({ 
          verification_status: verified ? 'verified' : 'rejected' 
        })
        .eq('id', childId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['children'] });
      toast.success('Verification status updated!');
    },
    onError: (error) => {
      logger.error('Verify child error', { message: error.message });
      toast.error(`Failed to update verification: ${error.message}`);
    }
  });
};

/**
 * Assign child to group (UPDATED: single group instead of array)
 */
export const useAssignChildToGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ childId, groupId }) => {
      const { data, error } = await supabase
        .from('children')
        .update({ group_id: groupId })  // ✅ CHANGED: single value, not array
        .eq('id', childId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['children'] });
      toast.success('Child assigned to group!');
    },
    onError: (error) => {
      logger.error('Assign child error', { message: error.message });
      toast.error(`Failed to assign child: ${error.message}`);
    }
  });
};

/**
 * Remove child from group (set group_id to null)
 */
export const useRemoveChildFromGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (childId) => {
      const { data, error } = await supabase
        .from('children')
        .update({ group_id: null })
        .eq('id', childId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['children'] });
      toast.success('Child removed from group!');
    },
    onError: (error) => {
      logger.error('Remove from group error', { message: error.message });
      toast.error(`Failed to remove child: ${error.message}`);
    }
  });
};

/**
 * Add parent to child
 */
export const useAddParentToChild = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ childId, parentId }) => {
      // First get current parent_ids
      const { data: child } = await supabase
        .from('children')
        .select('parent_ids')
        .eq('id', childId)
        .single();

      const currentParentIds = child?.parent_ids || [];
      
      // Check max 4 parents
      if (currentParentIds.length >= 4) {
        throw new Error('Maximum 4 parents per child allowed');
      }

      // Add new parent
      const updatedParentIds = [...currentParentIds, parentId];

      const { data, error } = await supabase
        .from('children')
        .update({ parent_ids: updatedParentIds })
        .eq('id', childId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['children'] });
      toast.success('Parent added to child!');
    },
    onError: (error) => {
      logger.error('Add parent error', { message: error.message });
      toast.error(`Failed to add parent: ${error.message}`);
    }
  });
};

/**
 * Remove parent from child
 */
export const useRemoveParentFromChild = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ childId, parentId }) => {
      // Get current parent_ids
      const { data: child } = await supabase
        .from('children')
        .select('parent_ids')
        .eq('id', childId)
        .single();

      const currentParentIds = child?.parent_ids || [];
      
      // Remove parent
      const updatedParentIds = currentParentIds.filter(id => id !== parentId);

      const { data, error } = await supabase
        .from('children')
        .update({ parent_ids: updatedParentIds })
        .eq('id', childId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['children'] });
      toast.success('Parent removed from child!');
    },
    onError: (error) => {
      logger.error('Remove parent error', { message: error.message });
      toast.error(`Failed to remove parent: ${error.message}`);
    }
  });
};
